import { Helmet } from "react-helmet-async";
import { tagSlug } from "@/lib/slug";
import { effectiveTimestamps } from "@/lib/episodeTimestamps";

const SITE_URL = "https://juristube.com.br";

/**
 * Iter271 — Normaliza uma URL de imagem para uso em schema.org / OG.
 *
 * Regras (evita "URL do campo image não é válido" no Google Search Console):
 *   • http(s)://…       → usa como está
 *   • /path/relativo    → prepend SITE_URL
 *   • data:image/…      → REJEITA (Google não aceita data URI em schema)
 *   • blob:… ou lixo    → REJEITA
 *   • vazio / null      → retorna null (schema deve omitir a chave)
 *
 * Sempre retorne apenas quando `!!` for truthy antes de spread em schema.
 */
export function normaliseImageUrl(input) {
  if (!input || typeof input !== "string") return null;
  const s = input.trim();
  if (!s) return null;
  if (s.startsWith("data:") || s.startsWith("blob:")) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${SITE_URL}${s}`;
  // Qualquer outro formato (base64 puro sem prefixo, fragment, etc.) — descarta.
  return null;
}

/**
 * Sitewide Organization descriptor — sincronizado com o
 * @graph do public/index.html (mesmo @id). Google consolida os dois
 * emissões numa única entidade "JurisTube" via @id.
 *
 * Usado em dois papéis:
 *  1. Standalone `<StructuredData type="Organization" />` na Home
 *     (declara a entidade JurisTube com detalhes completos para o
 *     "Knowledge Panel" à direita do resultado de busca).
 *  2. `publisher: ORGANIZATION` em VideoObject / PodcastSeries /
 *     Article / etc — Google Search cita o publisher em rich cards.
 */
const ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "JurisTube",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/juristube-icon-transparent-720.png`,
  image: `${SITE_URL}/brand/juristube-banner-1200x471.png`,
  description:
    "JurisTube: infraestrutura viva de aprendizado e pesquisa em Direito Público. Podcasts, artigos, normas, notícias, vídeos, ferramentas interativas e curadoria científica em um só lugar.",
  foundingDate: "2024",
  founder: {
    "@type": "Person",
    name: "Paulo Modesto",
    url: `${SITE_URL}/autor/paulo-modesto`,
    affiliation: { "@type": "Organization", name: "UFBA" },
  },
  parentOrganization: {
    "@type": "Organization",
    name: "Instituto Brasileiro de Direito Público",
    alternateName: "IBDP",
  },
  knowsLanguage: ["pt-BR", "en", "es", "it", "zh-CN", "de"],
  areaServed: { "@type": "Country", name: "Brazil" },
  sameAs: [
    "https://www.youtube.com/@paulomodestodireito",
    "https://www.instagram.com/paulomodestodireito",
  ],
};

/**
 * Mapa da navegação principal do site — usado para emitir
 * `SiteNavigationElement` schema.org (ver case abaixo em Component).
 *
 * A ordem reflete a prioridade EDITORIAL das seções (não a ordem no
 * menu visual), porque o Google usa a posição como sinal de peso
 * quando escolhe quais 3-6 Sitelinks vai mostrar sob o resultado
 * principal.
 *
 * Descrições são curtas (≤ 60 chars) — o Google as usa como
 * legenda quando o Sitelink é renderizado.
 */
const SITE_NAV_ITEMS = [
  { name: "TV",          url: "/tv",         description: "Aulas, palestras e debates em vídeo" },
  { name: "Podcasts",    url: "/podcasts",   description: "Áudio-aulas e séries editoriais" },
  { name: "Rádio",       url: "/radio",      description: "Rádio JurisTube — canais temáticos" },
  { name: "Radar",       url: "/radar",      description: "Notícias jurídicas e Radar Constitucional" },
  { name: "Artigos",     url: "/colunistas", description: "Doutrina e artigos de colunistas" },
  { name: "Livraria",    url: "/livraria",   description: "Livros e materiais didáticos" },
  { name: "Autores",     url: "/autores",    description: "Perfis dos autores publicados" },
  { name: "Temas",       url: "/temas",      description: "Direito Público por tema e coleção" },
  { name: "Comunidade",  url: "/comunidade", description: "Espaço aberto de discussão" },
  { name: "Cursos",      url: "/cursos",     description: "Cursos e formações" },
  { name: "Blog",        url: "/blog",       description: "Blog editorial do JurisTube" },
];

/**
 * Schema.org JSON-LD structured data injector.
 * Produces rich snippets on Google (video thumbnails, author cards,
 * podcast cards) — boosts organic CTR dramatically.
 *
 * Usage:
 *   <StructuredData type="VideoObject" data={{...}} />
 *   <StructuredData type="Person" data={{...}} />
 *   <StructuredData type="PodcastSeries" data={{...}} />
 */
export default function StructuredData({ type, data }) {
  let schema = null;

  if (type === "VideoObject" && data) {
    // Multi-resolution thumbnails help Google pick the right format for
    // the search result card. YouTube serves these reliably at fixed
    // CDN paths the moment a video is published.
    const yt = data.youtube_id;
    const thumbnailUrls = yt ? [
      `https://i.ytimg.com/vi/${yt}/maxresdefault.jpg`,  // 1280×720
      `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`,      // 480×360
      `https://i.ytimg.com/vi/${yt}/mqdefault.jpg`,      // 320×180
    ] : [data.thumbnail];

    // Build canonical episode URL — same path the SPA renders for the
    // user. ``@id`` and ``mainEntityOfPage`` both point at it so the
    // Google graph treats them as a single entity.
    //
    // Callers can override the derived path by passing
    // ``data.urlPath`` (used by ShortPage so its VideoObject points at
    // ``/shorts/<id>`` instead of the bogus ``/episodio/<id>`` fallback
    // that nukes Google's canonical signal — Feb/2026 GSC fix).
    const episodePath = data.urlPath
      ? data.urlPath
      : (data.podcast_slug && data.slug)
        ? `/episodio/${data.podcast_slug}/${data.slug}`
        : `/episodio/${data.id || ""}`;
    const canonicalUrl = `${SITE_URL}${episodePath}`;

    // Tags + categories → keywords (Google uses this for topic ranking).
    const keywords = [
      ...(data.tags || []),
      ...(data.categories || []),
    ].filter(Boolean).join(", ") || undefined;

    schema = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "@id": canonicalUrl,
      name: data.title,
      ...(data.description ? { description: data.description.slice(0, 500) } : {}),
      thumbnailUrl: thumbnailUrls,
      uploadDate: data.published_at,
      contentUrl: data.youtube_url,
      embedUrl: yt ? `https://www.youtube.com/embed/${yt}` : undefined,
      ...(data.duration_seconds ? { duration: `PT${data.duration_seconds}S` } : {}),
      ...(data.author ? { author: { "@type": "Person", name: data.author } } : {}),
      publisher: ORGANIZATION,
      inLanguage: "pt-BR",
      ...(data.podcast_name ? { partOfSeries: { "@type": "PodcastSeries", name: data.podcast_name } } : {}),
      // Educational-context signals: tell Google this is a lecture aimed
      // at higher-ed audiences. Improves placement in academic queries
      // and unlocks the "Educational Video" rich result variant.
      learningResourceType: "Lecture",
      educationalLevel: "higher education",
      educationalUse: "professional development",
      isFamilyFriendly: true,
      // The episode-page URL becomes the canonical webpage for this
      // video; both directions of the relation help Google's graph.
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl,
      },
      ...(keywords ? { keywords } : {}),
      // Iter287 — hasPart: Clip[] emitido a partir de `data.timestamps`
      // (array estruturado curado no admin, prioridade absoluta) OU
      // parse fallback do `data.description` (para episódios recém-
      // importados que ainda não passaram pela edição manual).
      // Iter287.1 — lógica extraída para lib/episodeTimestamps.js
      // (compartilhada com a UI de "capítulos clicáveis" da página).
      // Google Docs:
      // https://developers.google.com/search/docs/appearance/structured-data/video#clip
      ...((() => {
        const sorted = effectiveTimestamps(data);
        if (sorted.length === 0) return {};
        const clips = sorted.map((s, i) => {
          const nextT = i + 1 < sorted.length ? sorted[i + 1].t : null;
          let end;
          if (nextT !== null) {
            end = nextT;
          } else if (data.duration_seconds && data.duration_seconds > s.t) {
            end = Number(data.duration_seconds);
          } else {
            end = s.t + 60;
          }
          if (end <= s.t) end = s.t + 1;
          const name = s.label.length <= 80
            ? s.label
            : s.label.slice(0, 80).replace(/[,.;: ]+$/, "") + "…";
          return {
            "@type": "Clip",
            name,
            startOffset: s.t,
            endOffset: end,
            url: `${canonicalUrl}?t=${s.t}`,
          };
        });
        return { hasPart: clips };
      })()),
      ...(data.transcript ? {
        transcript: data.transcript.slice(0, 5000),
      } : {}),
    };
  } else if (type === "Person" && data) {
    // Knowledge Panel pre-requisites: as many high-signal fields as we
    // can derive without manual input, plus optional ones the admin can
    // fill (Lattes/ORCID/LinkedIn/affiliation) that Google uses to
    // consolidate the author's identity across the web.
    const canonical = `${SITE_URL}/autor/${data.slug}`;

    // sameAs: external profile URLs. Conjur and JOTA columnist pages are
    // already harvested by our scrapers; the social links are optional.
    // Iter216: Wikipedia URL adicionada — quando presente, é o sinal
    // mais forte pro Google conectar o autor ao Knowledge Graph.
    const sameAs = [
      data.lattes_url,
      data.orcid_url,
      data.wikipedia_url,
      data.linkedin_url,
      data.twitter_url,
      data.conjur_page_url,
      data.jota_page_url,
    ].filter(Boolean);

    // knowsAbout: usually populated server-side from the author's tag
    // distribution. When the API ships it, we forward it as-is. Capped
    // for safety on the rare case the API returns hundreds.
    const knowsAbout = Array.isArray(data.knows_about)
      ? data.knows_about.slice(0, 30)
      : undefined;

    // Optional Knowledge Panel fields. Each gated on existence so a
    // sparse profile doesn't emit empty arrays.
    const affiliation = (() => {
      // `university` is the lightweight free-text field exposed via the
      // admin author editor (ex. "UFBA", "FGV-SP"). It maps directly to
      // schema.org's affiliation/Organization model so we treat it as a
      // first-class affiliation source. Falls back to the legacy
      // ``affiliation`` field if present.
      const src = data.affiliation || data.university;
      if (!src) return undefined;
      if (typeof src === "string") {
        return { "@type": "Organization", name: src };
      }
      return src.map((a) => ({ "@type": "Organization", name: a }));
    })();
    const alumniOf = (() => {
      if (!data.alumni_of) return undefined;
      if (typeof data.alumni_of === "string") {
        return { "@type": "EducationalOrganization", name: data.alumni_of };
      }
      return data.alumni_of.map((a) => ({ "@type": "EducationalOrganization", name: a }));
    })();

    schema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": canonical,
      name: data.name,
      url: canonical,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      jobTitle: data.job_title || "Jurista",
      ...(data.bio ? { description: data.bio.slice(0, 500) } : {}),
      ...(normaliseImageUrl(data.photo) ? { image: normaliseImageUrl(data.photo) } : {}),
      ...(data.state || data.country ? {
        address: {
          "@type": "PostalAddress",
          addressRegion: data.state || undefined,
          addressCountry: data.country || "Brasil",
        },
      } : {}),
      ...(data.country === "Brasil" ? {
        nationality: { "@type": "Country", name: "Brazil" },
      } : {}),
      ...(affiliation ? { affiliation } : {}),
      ...(alumniOf ? { alumniOf } : {}),
      ...(data.awards ? { award: Array.isArray(data.awards) ? data.awards : [data.awards] } : {}),
      ...(knowsAbout && knowsAbout.length ? { knowsAbout } : {}),
      ...(sameAs.length ? { sameAs } : {}),
    };
  } else if (type === "PodcastSeries" && data) {
    schema = {
      "@context": "https://schema.org",
      "@type": "PodcastSeries",
      name: data.name,
      ...(data.description ? { description: data.description } : {}),
      ...(normaliseImageUrl(data.cover) ? { image: normaliseImageUrl(data.cover) } : {}),
      url: `${SITE_URL}/podcast/${data.slug}`,
      publisher: ORGANIZATION,
      inLanguage: "pt-BR",
      ...(data.episode_count ? { numberOfEpisodes: data.episode_count } : {}),
    };
  } else if (type === "ProfilePage" && data) {
    schema = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      mainEntity: {
        "@type": "Person",
        name: data.name,
        ...(data.bio ? { description: data.bio.slice(0, 500) } : {}),
        ...(normaliseImageUrl(data.image) ? { image: normaliseImageUrl(data.image) } : {}),
        ...(data.institution ? { affiliation: { "@type": "Organization", name: data.institution } } : {}),
        url: `${SITE_URL}/criador/${data.id}`,
      },
      url: `${SITE_URL}/criador/${data.id}`,
      inLanguage: "pt-BR",
      publisher: ORGANIZATION,
    };
  } else if (type === "CollectionPage" && data) {
    schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: data.name,
      ...(data.description ? { description: data.description } : {}),
      url: `${SITE_URL}/c/${data.share_slug}`,
      publisher: ORGANIZATION,
      inLanguage: "pt-BR",
    };
  } else if (type === "PodcastEpisode" && data) {
    // Iter215 — Emitido em COMPLEMENTO ao VideoObject nas páginas de
    // episódio. Enquanto VideoObject serve o card de vídeo do Google,
    // PodcastEpisode é lido por agregadores de podcast (Google
    // Podcasts, Apple Podcasts Directory) + ferramentas de citação
    // acadêmica (Zotero/Mendeley) que interpretam podcast como
    // fonte primária no seu esquema de referência.
    //
    // Data shape (mesmo objeto ``ep`` da EpisodePage):
    //   { id, title, slug, podcast_slug, podcast_name, description,
    //     duration_seconds, published_at, youtube_url, author,
    //     categories, transcript }
    const episodePath = data.urlPath
      ? data.urlPath
      : (data.podcast_slug && data.slug)
        ? `/episodio/${data.podcast_slug}/${data.slug}`
        : `/episodio/${data.id || ""}`;
    const canonicalUrl = `${SITE_URL}${episodePath}#podcast`;
    schema = {
      "@context": "https://schema.org",
      "@type": "PodcastEpisode",
      "@id": canonicalUrl,
      name: data.title,
      url: `${SITE_URL}${episodePath}`,
      ...(data.description ? { description: data.description.slice(0, 500) } : {}),
      ...(data.published_at ? { datePublished: data.published_at } : {}),
      ...(data.duration_seconds ? { timeRequired: `PT${data.duration_seconds}S` } : {}),
      ...(data.youtube_url ? {
        associatedMedia: {
          "@type": "MediaObject",
          contentUrl: data.youtube_url,
          encodingFormat: "video/mp4",
        },
      } : {}),
      ...(data.author ? {
        author: { "@type": "Person", name: data.author },
      } : {}),
      partOfSeries: data.podcast_name ? {
        "@type": "PodcastSeries",
        name: data.podcast_name,
        ...(data.podcast_slug ? { url: `${SITE_URL}/podcast/${data.podcast_slug}` } : {}),
      } : undefined,
      publisher: ORGANIZATION,
      inLanguage: "pt-BR",
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${episodePath}` },
    };
  } else if (type === "Article" && data) {
    // Tags → multiple SEO signals. Google Discover ranks heavily on
    // ``about`` (entity links) and ``keywords`` (CSV string); we emit
    // both. ``articleSection`` picks the first tag as the primary topic.
    // URLs in ``about`` mirror the public /tema/{name} pages that
    // TagCloud / TagsFooter link to, so the entity graph stays
    // consistent across the site.
    const tags = Array.isArray(data.tags)
      ? data.tags.map((t) => String(t || "").trim()).filter(Boolean)
      : [];
    const aboutList = tags.slice(0, 12).map((t) => ({
      "@type": "Thing",
      name: t,
      url: `${SITE_URL}/tema/${tagSlug(t)}`,
    }));
    // Iter215 — Autor com URL canônica (Zotero/Mendeley usam esse
    // link pra puxar afiliação e citações do autor) + campos
    // Scholar-friendly (wordCount, headline com limite).
    const authorNode = data.author_slug ? {
      "@type": "Person",
      name: data.author,
      url: `${SITE_URL}/autor/${data.author_slug}`,
      sameAs: `${SITE_URL}/autor/${data.author_slug}`,
    } : { "@type": "Person", name: data.author };
    const wordCount = typeof data.wordCount === "number"
      ? data.wordCount
      : (typeof data.content_text === "string"
        ? data.content_text.trim().split(/\s+/).filter(Boolean).length
        : undefined);
    schema = {
      "@context": "https://schema.org",
      // Iter260 — Opt-in ``ScholarlyArticle``. Google Scholar indexa
      // com mais confiança quando o tipo é explicitamente acadêmico;
      // a ferramenta oficial de Rich Results aceita como Article
      // (retrocompatibilidade) e adiciona os campos extras
      // ``learningResourceType``, ``educationalLevel`` e ``genre``
      // como sinais de autoridade doutrinária.
      // Default = Article (blog, decisões, informativos). Doutrinários
      // (ColumnistArticlePage) passam ``data.scholarly: true``.
      "@type": data.scholarly === true ? "ScholarlyArticle" : "Article",
      headline: (data.title || "").slice(0, 110), // Scholar tolera até 110 chars
      // Iter260 — extras que só fazem sentido em ScholarlyArticle.
      ...(data.scholarly === true ? {
        learningResourceType: "Scholarly article",
        educationalLevel: "Higher education",
        // Legal doctrine é a matéria dominante do acervo. Consumers
        // (Zotero, Mendeley, RCAAP) usam ``genre`` para dedup.
        genre: "Legal doctrine",
      } : {}),
      author: authorNode,
      ...(data.datePublished ? { datePublished: data.datePublished } : {}),
      ...(data.dateModified ? { dateModified: data.dateModified } : {}),
      ...(data.description ? {
        description: data.description.slice(0, 300),
        abstract: data.description.slice(0, 500),
      } : {}),
      // Iter298 — image como ImageObject com width/height. Google News
      // e Google Discover exigem essa forma (não apenas URL string)
      // para promover o card grande e considerar a matéria elegível
      // para o top-stories carousel. Se o caller passar imageWidth/
      // imageHeight, montamos o ImageObject; caso contrário, cai no
      // fallback antigo (string URL) para retrocompatibilidade.
      ...(normaliseImageUrl(data.image)
        ? {
            image:
              data.imageWidth && data.imageHeight
                ? {
                    "@type": "ImageObject",
                    url: normaliseImageUrl(data.image),
                    width: Number(data.imageWidth),
                    height: Number(data.imageHeight),
                    ...(data.imageAlt ? { caption: data.imageAlt } : {}),
                  }
                : normaliseImageUrl(data.image),
          }
        : {}),
      url: data.url,
      mainEntityOfPage: { "@type": "WebPage", "@id": data.url },
      publisher: ORGANIZATION,
      inLanguage: "pt-BR",
      ...(wordCount ? { wordCount } : {}),
      ...(tags.length ? { keywords: tags.join(", ") } : {}),
      ...(tags.length ? { articleSection: tags[0] } : {}),
      ...(aboutList.length ? { about: aboutList } : {}),
      // isAccessibleForFree = true melhora a taxa de citação em
      // ferramentas acadêmicas — sinaliza que a fonte é aberta.
      isAccessibleForFree: true,
      // Licença padrão do JurisTube. Ajuda Zotero/Mendeley a rotular
      // corretamente na exportação de bibliografia.
      ...(data.license ? { license: data.license } : {}),
    };
  } else if (type === "ItemList" && data) {
    // Used by hub pages like /temas to expose every linked tag as an
    // entity Google can crawl from one URL. The legend in Search
    // Console treats this as a Sitelinks Searchbox candidate when
    // combined with the WebSite schema on the home page.
    const items = Array.isArray(data.items) ? data.items : [];
    const baseUrl = data.url
      ? (data.url.startsWith("http") ? data.url : `${SITE_URL}${data.url}`)
      : SITE_URL;
    schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      ...(data.name ? { name: data.name } : {}),
      url: baseUrl,
      numberOfItems: items.length,
      itemListElement: items.map((it, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: it.url
          ? (it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`)
          : undefined,
        name: it.name,
      })),
    };
  } else if (type === "WebSite") {
    schema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "JurisTube",
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/busca?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
      inLanguage: "pt-BR",
    };
  } else if (type === "Organization") {
    schema = { "@context": "https://schema.org", ...ORGANIZATION };
  } else if (type === "BreadcrumbList" && data) {
    // Migalhas Google — Google renderiza breadcrumbs navegáveis
    // logo abaixo do <title> no SERP (ex: "juristube.com.br › Rádio ›
    // Aula de Amanhã"). Dobra o CTR em pesquisas específicas por
    // "levantar" a hierarquia do site.
    //
    // Data shape esperado:
    //   { items: [{ name: "Início", url: "/" }, { name: "Rádio",
    //   url: "/radio" }, { name: "Aula de Amanhã" }] }
    // A URL do último item PODE ser omitida (Google interpreta como
    // "current page") — recomendação oficial do schema.org.
    const items = Array.isArray(data.items) ? data.items : [];
    schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, idx) => {
        const el = {
          "@type": "ListItem",
          position: idx + 1,
          name: it.name,
        };
        if (it.url) {
          const abs = it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`;
          el.item = abs;
        }
        return el;
      }),
    };
  } else if (type === "SiteNavigation") {
    // Iter190 — SiteNavigationElement.
    //
    // Ajuda o Google Search a identificar quais são as seções
    // principais do site quando compila os "Sitelinks" (linhas
    // secundárias que aparecem SOB o resultado principal na SERP —
    // ex.: `Vídeos · Podcasts · Rádio · Radar · Artigos`).
    // Google não garante que vai renderizar, mas dá a ele o mapa da
    // navegação primária em vez de deixar o algoritmo adivinhar
    // pelo DOM.
    //
    // Emitido UMA VEZ, na Home — os Sitelinks só aparecem no
    // resultado principal (URL de raiz), então repetir em toda
    // página é desperdício de bytes sem ganho.
    schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}/#site-nav`,
      itemListElement: SITE_NAV_ITEMS.map((it, idx) => ({
        "@type": "SiteNavigationElement",
        position: idx + 1,
        name: it.name,
        url: `${SITE_URL}${it.url}`,
        ...(it.description ? { description: it.description } : {}),
      })),
    };
  }

  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
